import { useState } from "react";
import type { HandState } from "@joker-holdem/core";
import { useGameStore, HERO, BOT } from "../store/gameStore.js";
import { RecentHandsBar } from "../components/RecentHandsBar.js";
import { BoardView } from "../components/BoardView.js";
import { PlayerPanel } from "../components/PlayerPanel.js";
import { CardView } from "../components/CardView.js";
import { ActionBar } from "../components/ActionBar.js";
import { BetAmountInput } from "../components/BetAmountInput.js";
import { HandDetailModalLoader } from "../components/HandDetailModalLoader.js";
import { isPreflopFoldResult } from "../engine/selectors.js";
import { useBetAmount } from "../engine/useBetAmount.js";

const NAMES: readonly [string, string] = ["あなた", "Bot"];

/**
 * Bot's hole cards, in the center of its panel (docs/UI_REFERENCE.md
 * "フィードバック反映" 2026-07-17 #1): face-down backs while the hand is
 * live (so it's visually obvious the bot holds a hand), revealed once the
 * hand is decided -- by fold or by showdown, always (bot opponent, no
 * privacy to protect).
 */
function OpponentHoleCards({ hand }: { hand: HandState }) {
  const bot = hand.players[BOT];
  const revealed = hand.result !== null;
  if (!revealed && bot.folded) return null;
  return revealed ? (
    <>
      <CardView card={bot.hole[0]} size="sm" />
      <CardView card={bot.hole[1]} size="sm" />
    </>
  ) : (
    <>
      <CardView back size="sm" />
      <CardView back size="sm" />
    </>
  );
}

export function PlayScreen() {
  const hand = useGameStore((s) => s.hand);
  const recentHands = useGameStore((s) => s.recentHands);
  const botThinking = useGameStore((s) => s.botThinking);
  const awaitingAdvance = useGameStore((s) => s.awaitingAdvance);
  const matchOver = useGameStore((s) => s.matchOver);
  const heroAct = useGameStore((s) => s.heroAct);
  const advanceToNextHand = useGameStore((s) => s.advanceToNextHand);
  const goHistory = useGameStore((s) => s.goHistory);
  const goStart = useGameStore((s) => s.goStart);
  const rematch = useGameStore((s) => s.rematch);
  const [detailId, setDetailId] = useState<number | null>(null);
  // Lifted here (rather than inside ActionBar) so the amount input can be
  // rendered in the hero's PlayerPanel instead (2026-07-17 フィードバック
  // #3) while still driving the same value ActionBar's presets/buttons use.
  // Called unconditionally, before the `!hand` early return below, per the
  // rules of hooks -- see useBetAmount's `hand: HandState | null` param.
  const betAmount = useBetAmount(hand);

  if (!hand) return null;

  const heroHole = hand.players[HERO].hole;
  // Preflop-fold hands auto-advance instantly and never show the result UI
  // (フィードバック #2 例外) -- treat them the same as a still-live hand.
  // Exception to the exception: once the match itself is over there's no
  // auto-advance to skip ahead to (2026-07-17 フィードバック #2), so the
  // result UI (and its match-end buttons) must show even after a
  // match-ending preflop fold.
  const showResult = hand.result !== null && (matchOver || !isPreflopFoldResult(hand));
  const heroTurn = hand.result === null && hand.toAct === HERO;
  // The amount input only makes sense (and only reserves its slot) while
  // it's the hero's turn and a bet/raise is actually legal -- hidden
  // otherwise, but the hero panel's height never changes either way (its
  // center column is otherwise empty, unlike the bot panel's hole cards).
  const showBetInput = heroTurn && !!betAmount.open;

  return (
    <div className="screen play-screen">
      <div className="play-header">
        <RecentHandsBar hands={recentHands} onSelect={setDetailId} />
        <button type="button" className="history-link-btn" onClick={goHistory}>
          履歴
        </button>
      </div>

      <PlayerPanel hand={hand} seat={BOT} name={NAMES[BOT]}>
        <OpponentHoleCards hand={hand} />
      </PlayerPanel>

      <BoardView hand={hand} oppSeat={BOT} heroHole={heroHole} />

      <PlayerPanel hand={hand} seat={HERO} name={NAMES[HERO]} compact={showResult}>
        {showBetInput && betAmount.open && (
          <BetAmountInput
            min={betAmount.open.min}
            max={betAmount.open.max}
            value={betAmount.betValue}
            onChange={betAmount.setBetValue}
          />
        )}
      </PlayerPanel>

      {/* Item 4: exactly one of these three is ever interactive, but they
          all share this single fixed-height zone (see .bottom-zone) so
          switching between hero's turn / bot's turn / result never moves
          the table felt or board above it. */}
      <div className="bottom-zone">
        {heroTurn && <ActionBar hand={hand} betAmount={betAmount} onAction={heroAct} />}
        {!heroTurn && !showResult && (
          <div className="bottom-zone-status">{botThinking ? "Bot 思考中…" : " "}</div>
        )}
        {showResult && matchOver && (
          // Match-end buttons (2026-07-17 フィードバック #2): replaces the old
          // dedicated MatchEndScreen -- the final hand's result stays on
          // screen (board/showdown and all), with these two stacked
          // vertically in the same fixed-height bottom-zone every other
          // state shares.
          <div className="match-end-buttons">
            <button type="button" className="next-hand-btn" onClick={() => rematch()}>
              次のハンド
            </button>
            <button type="button" className="match-end-secondary-btn" onClick={() => goStart()}>
              タイトルに戻る
            </button>
          </div>
        )}
        {showResult && !matchOver && (
          <button
            type="button"
            className="next-hand-btn"
            onClick={() => {
              if (awaitingAdvance) advanceToNextHand();
            }}
          >
            次のハンド
          </button>
        )}
      </div>

      {detailId !== null && (
        <HandDetailModalLoader id={detailId} names={NAMES} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}
